import { attachmentSetError, createEmailAttachmentDraft } from '@/hooks/email-attachment-draft';
import { useInvitationStoreSelector } from '@/contexts/invitation-context';
import type { InvitationMethod } from '@/types/invitation';
import type {
  BatchAttachmentInfo,
  TemplateLibraryEntry,
  TemplateLibraryLanguage,
  TemplatePair,
} from '@fluenceur/types';
import { useCallback, useMemo, useState } from 'react';

import { buildSendTemplateSeed } from '@/components/templates/build-send-template-seed';
import {
  useInvitationTemplateSelector,
  type AppliedTemplate,
} from './use-invitation-template-selector';
import { methodToPair } from './send-invitation-templates';

interface UseInvitationTemplateFlowArgs {
  brandId: string | undefined;
  /**
   * Send mode backing the template library. Optional so scopes with no
   * {@link InvitationMethod} (the sponsored collab proposal email, on the
   * EMAIL/COLLAB pair) can pass `pair` directly instead.
   */
  method?: InvitationMethod;
  /** Explicit (channel, context) override; takes precedence over `method`. */
  pair?: TemplatePair | null;
  language: TemplateLibraryLanguage;
  /** True while the editor body still matches the loaded template (no manual edit). */
  isEditorUntouched: boolean;
  onApplyAttachments?: (files: BatchAttachmentInfo[]) => boolean | Promise<boolean>;
}

interface UseInvitationTemplateFlowResult {
  entries: TemplateLibraryEntry[];
  isLoading: boolean;
  selectedEntryId: string | null;
  /** Brand template id to attribute on send, or null for a system default. */
  selectedTemplateId: string | null;
  selectEntry: (entryId: string) => void;
  isSaveDrawerOpen: boolean;
  openSaveDrawer: () => void;
  closeSaveDrawer: () => void;
  /** Seed values for the embedded save-as-template form. */
  saveSeed: ReturnType<typeof buildSendTemplateSeed> | null;
  onTemplateSaved: (entry: TemplateLibraryEntry) => void;
  attachmentsCustomized: boolean;
  attachmentsBlocked: boolean;
  attachmentsBusy: boolean;
}

/**
 * Glue between the template selector, the invitation editor store and the
 * save-as-template drawer. Applying a template writes its body into the store
 * (respecting the untouched-edit guard) and tracks the attribution id; saving a
 * new template auto-selects it once the refreshed library lists it.
 *
 * Side effects live here so the view stays a thin presentational shell.
 */
export const useInvitationTemplateFlow = ({
  brandId,
  method,
  pair,
  language,
  isEditorUntouched,
  onApplyAttachments,
}: UseInvitationTemplateFlowArgs): UseInvitationTemplateFlowResult => {
  // Single pair resolution shared by the selector and the save seed: an explicit
  // pair wins, otherwise it derives from the send mode.
  const resolvedPair = useMemo(
    () => pair ?? (method ? methodToPair(method) : null),
    [pair, method]
  );
  const setMessageSnapshot = useInvitationStoreSelector((state) => state.setMessageSnapshot);
  const setOriginalTemplate = useInvitationStoreSelector((state) => state.setOriginalTemplate);
  const setCompiledMessage = useInvitationStoreSelector((state) => state.setCompiledMessage);

  const attachmentDraft = useInvitationStoreSelector((state) => state.emailAttachmentDraft);
  const updateAttachmentDraft = useInvitationStoreSelector(
    (state) => state.updateEmailAttachmentDraft
  );
  const attachmentsBlocked =
    !!attachmentDraft &&
    (attachmentDraft.uploading ||
      !!attachmentDraft.deletingId ||
      attachmentDraft.issues.length > 0 ||
      !!attachmentSetError(attachmentDraft.attachments));

  const [isSaveDrawerOpen, setIsSaveDrawerOpen] = useState(false);

  const handleTemplateApplied = useCallback(
    (applied: AppliedTemplate) => {
      const applyBody = (accepted: boolean) => {
        if (!accepted) return false;
        updateAttachmentDraft?.((draft) => ({
          ...draft,
          templateEntryId: applied.entry.id,
          templateScope: `${applied.entry.channel}:${applied.entry.context}`,
        }));
        if (applied.body) {
          setOriginalTemplate(applied.body);
          setMessageSnapshot(applied.body);
          setCompiledMessage('');
        }
        return true;
      };
      if (onApplyAttachments) {
        const result = onApplyAttachments(applied.entry.attachments ?? []);
        return result instanceof Promise ? result.then(applyBody) : applyBody(result);
      }
      updateAttachmentDraft?.(() => createEmailAttachmentDraft(applied.entry.attachments ?? []));
      return applyBody(true);
    },
    [
      setOriginalTemplate,
      setMessageSnapshot,
      setCompiledMessage,
      updateAttachmentDraft,
      onApplyAttachments,
    ]
  );

  const selector = useInvitationTemplateSelector({
    brandId,
    method,
    pair: resolvedPair,
    language,
    autoSelectDefault: isEditorUntouched && !attachmentDraft?.customized,
    initialSelectedEntryId:
      resolvedPair &&
      attachmentDraft?.templateScope === `${resolvedPair.channel}:${resolvedPair.context}`
        ? attachmentDraft.templateEntryId
        : null,
    onTemplateApplied: handleTemplateApplied,
  });

  const { selectWhenAvailable } = selector;

  const openSaveDrawer = useCallback(() => {
    if (!attachmentsBlocked) setIsSaveDrawerOpen(true);
  }, [attachmentsBlocked]);
  const closeSaveDrawer = useCallback(() => setIsSaveDrawerOpen(false), []);

  const onTemplateSaved = useCallback(
    (entry: TemplateLibraryEntry) => {
      // The library cache is invalidated by the create action; select the new
      // entry as soon as the refetch surfaces it.
      selectWhenAvailable(entry.id);
    },
    [selectWhenAvailable]
  );

  const messageSnapshot = useInvitationStoreSelector((state) => state.messageSnapshot);
  const compiledMessage = useInvitationStoreSelector((state) => state.compiledMessage);

  const saveSeed = useMemo(() => {
    // Lazy: the seed (which parses the Lexical snapshot) is only needed while
    // the save drawer is open — not on every keystroke that moves the snapshot.
    if (!isSaveDrawerOpen) {
      return null;
    }
    if (!resolvedPair) {
      return null;
    }
    return buildSendTemplateSeed({
      pair: resolvedPair,
      language,
      content: compiledMessage,
      messageSnapshot,
      attachments: attachmentDraft?.attachments,
    });
  }, [
    isSaveDrawerOpen,
    resolvedPair,
    language,
    compiledMessage,
    messageSnapshot,
    attachmentDraft?.attachments,
  ]);

  return {
    entries: selector.entries,
    isLoading: selector.isLoading,
    selectedEntryId: selector.selectedEntryId,
    selectedTemplateId: selector.selectedTemplateId,
    selectEntry: selector.selectEntry,
    isSaveDrawerOpen,
    openSaveDrawer,
    closeSaveDrawer,
    saveSeed,
    onTemplateSaved,
    attachmentsCustomized: attachmentDraft?.customized ?? false,
    attachmentsBlocked,
    attachmentsBusy:
      !!attachmentDraft && (attachmentDraft.uploading || !!attachmentDraft.deletingId),
  };
};
